// HighlightsView — library-side "Notebook" view for a single book.
//
// Lists every highlight the user has saved inside a book, grouped by
// chapter and rendered with the original color band. Tapping a highlight
// drops a one-shot CFI into sessionStorage and navigates to the reader,
// which honors it via the existing `pv.boot.cfi.{bookId}` channel — same
// channel the bookmark button uses.
//
// This is the "Amazon notebook" vision (2026-06-26): highlights are
// first-class data, not just an in-reader annotation. The next loop on
// this feature will add Kindle My-Clippings.txt import and cloud sync so
// highlights survive a PWA reinstall.

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "@/services/db";
import { downloadMarkdown, deleteHighlight } from "@/services/notesService";
import { HIGHLIGHT_COLORS } from "@/types/highlight";
import type { Book } from "@/types/book";
import type { Highlight } from "@/types/highlight";

function colorHex(id: string): string {
  return HIGHLIGHT_COLORS.find((c) => c.id === id)?.hex ?? "#ffec6b";
}

function relativeDate(ms: number): string {
  const days = Math.floor((Date.now() - ms) / 86_400_000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return new Date(ms).toISOString().slice(0, 10);
}

export function HighlightsView() {
  const { bookId } = useParams<{ bookId: string }>();
  const navigate = useNavigate();
  const [book, setBook] = useState<Book | undefined>(undefined);

  useEffect(() => {
    if (!bookId) return;
    let cancelled = false;
    (async () => {
      const b = await db.books.get(bookId);
      if (!cancelled) setBook(b);
    })();
    return () => {
      cancelled = true;
    };
  }, [bookId]);

  const highlights: Highlight[] = useLiveQuery(
    () =>
      bookId
        ? db.highlights.where("bookId").equals(bookId).sortBy("createdAt")
        : Promise.resolve([] as Highlight[]),
    [bookId],
    [] as Highlight[],
  );

  // Group by chapter label (falling back to href, then "Untitled"). Preserves
  // creation order within each chapter so the user sees their notes in the
  // order they made them.
  const grouped: Array<{ chapter: string; items: Highlight[] }> = [];
  {
    const order: string[] = [];
    const map = new Map<string, Highlight[]>();
    for (const h of highlights) {
      const key = h.chapterLabel || h.href || "Untitled";
      if (!map.has(key)) {
        map.set(key, []);
        order.push(key);
      }
      map.get(key)!.push(h);
    }
    for (const key of order) {
      grouped.push({ chapter: key, items: map.get(key)! });
    }
  }

  const onOpenAt = (h: Highlight) => {
    if (!bookId) return;
    // The reader's boot handshake reads start CFI from a per-book session
    // key. Drop the highlight's anchor and let the reader's normal mount
    // flow open at it (handles all the spine-href + retry plumbing).
    try {
      // cfiRange is the SELECTION range; the reader wants a START CFI. Use
      // the first endpoint by stripping the comma-extension tail.
      const startCfi = h.cfiRange.replace(/,.*\)$/, ")");
      sessionStorage.setItem(`pv.boot.cfi.${bookId}`, startCfi);
    } catch {
      /* session storage blocked — reader will fall back to last position */
    }
    navigate(`/reader/${bookId}`);
  };

  const onDelete = async (h: Highlight) => {
    if (!confirm(`Delete this highlight?\n\n"${h.text.slice(0, 80)}…"`)) return;
    await deleteHighlight(h.id);
  };

  const onExport = async () => {
    if (!book) return;
    await downloadMarkdown(book.id, book.title, book.author);
  };

  return (
    <div
      className="pv-fg flex h-full flex-col"
      style={{
        backgroundColor: "var(--pv-bg)",
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      {/* Top bar — back · title · export */}
      <div
        className="pv-border flex items-center gap-2 border-b px-3 py-2"
        style={{ backgroundColor: "var(--pv-surface)" }}
      >
        <Link
          to="/library"
          aria-label="Home"
          className="pv-fg-soft active:opacity-60 flex items-center px-2 py-1"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M3 11.5 12 4l9 7.5" />
            <path d="M5 10v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9" />
            <path d="M10 20v-5h4v5" />
          </svg>
        </Link>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="pv-fg-soft text-[10px] uppercase tracking-wide">
            Highlights
          </div>
          <div className="pv-fg truncate text-sm font-semibold">
            {book?.title ?? "…"}
          </div>
        </div>
        <button
          type="button"
          onClick={onExport}
          disabled={highlights.length === 0}
          aria-label="Export as Markdown"
          className="pv-fg-soft px-3 py-1 text-xs disabled:opacity-30"
        >
          Export
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-3 py-3">
        {highlights.length === 0 ? (
          <div className="pv-fg-soft mt-16 px-4 text-center text-sm">
            <p className="mb-3">No highlights yet.</p>
            <p className="text-xs leading-relaxed">
              Open the book, press and hold any passage to select it,
              then tap a color in the popover. Your highlights will land
              here, grouped by chapter.
            </p>
            <Link
              to={`/reader/${bookId}`}
              className="pv-accent-bg mt-6 inline-block rounded-full px-4 py-2 text-xs font-semibold"
            >
              Open book
            </Link>
          </div>
        ) : (
          grouped.map((g) => (
            <section key={g.chapter} className="mb-5">
              <h2 className="pv-fg-soft mb-2 text-[11px] font-semibold uppercase tracking-wide">
                {g.chapter}
              </h2>
              <ul className="space-y-2">
                {g.items.map((h) => (
                  <li
                    key={h.id}
                    className="pv-surface pv-border relative overflow-hidden rounded-md border"
                  >
                    {/* Color band — left edge in the user's chosen highlight tint */}
                    <div
                      className="absolute left-0 top-0 h-full w-1.5"
                      style={{ backgroundColor: colorHex(h.color) }}
                      aria-hidden
                    />
                    <button
                      type="button"
                      onClick={() => onOpenAt(h)}
                      className="block w-full text-left px-3 py-3 pl-4 active:opacity-70"
                    >
                      <p
                        className="pv-fg text-[15px] leading-snug"
                        style={{ fontFamily: "Georgia, serif" }}
                      >
                        {h.text}
                      </p>
                      {h.note && (
                        <p className="pv-fg-soft mt-2 text-xs italic">
                          {h.note}
                        </p>
                      )}
                      <div className="pv-fg-soft mt-2 flex items-center justify-between text-[10px] uppercase tracking-wide">
                        <span>{relativeDate(h.createdAt)}</span>
                        <span
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            void onDelete(h);
                          }}
                          className="px-2 py-1 text-[10px] normal-case opacity-60"
                        >
                          Delete
                        </span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </div>
    </div>
  );
}
